import React from "react";

import { Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";

const useStyles = makeStyles((theme) => ({
	ticketHeader: {
		display: "flex",
		flex: "none",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "10px 16px",
		gap: 8,
		position: "relative",
		backgroundColor:
			theme.palette.type === "dark" ? "#131614" : "#ffffff",
		borderBottom:
			theme.palette.type === "dark"
				? "1px solid rgba(255,255,255,0.06)"
				: "1px solid rgba(0,0,0,0.06)",
		// Brand stripe along the top edge — keeps the chat shell visually
		// tied to the Settings / Tasks redesigns without dominating the
		// header's information density.
		//
		// Gradient direction flips with `theme.direction`: in Hebrew/Arabic
		// (RTL) the user's eye enters from the right, so the saturated
		// green needs to start on the right and fade to transparent on
		// the left. The previous fixed 90deg made the stripe fade into a
		// white sliver at the top-left corner of the chat shell in RTL,
		// which read as a layout glitch rather than a styled accent.
		"&::before": {
			content: '""',
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			height: 2,
			background:
				theme.direction === "rtl"
					? "linear-gradient(270deg, #25d366 0%, #128c7e 60%, transparent 100%)"
					: "linear-gradient(90deg, #25d366 0%, #128c7e 60%, transparent 100%)",
			opacity: theme.palette.type === "dark" ? 0.55 : 0.7,
		},
		// Mobile: stack the customer card above the action row.
		[theme.breakpoints.down("xs")]: {
			flexDirection: "column",
			alignItems: "stretch",
			padding: 0,
			gap: 0,
			borderBottom: "none",
			boxShadow:
				theme.palette.type === "dark"
					? "0 2px 12px rgba(255,255,255,0.06)"
					: "0 2px 12px rgba(0,0,0,0.06)",
		},
		// While the on-screen keyboard is up (html.kb-open is toggled by
		// initKeyboardViewport), hide the contact header so the shrunk shell
		// gives all its room to the messages + composer; it returns the moment
		// the keyboard closes. Inert on desktop (kb-open never fires there).
		"html.kb-open &": {
			display: "none",
		},
	},
	// Skeleton rows mirror the real header's geometry (TicketInfo contact
	// block + action-buttons row) so the loading state occupies the same
	// footprint on both the single-row desktop layout and the two-row
	// stacked mobile layout — otherwise content landing causes a CLS jump.
	skeletonContact: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		minHeight: 46, // avatar 42 + TicketInfo's 2px vertical padding
		padding: "2px 8px",
		[theme.breakpoints.down("xs")]: {
			minHeight: 40, // avatar 32 + 4px vertical padding
			padding: "4px 12px",
		},
	},
	skeletonActions: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 8,
		[theme.breakpoints.down("xs")]: {
			minHeight: 40, // 32px controls + 4px vertical padding
			padding: "4px 8px",
		},
	},
}));

const TicketHeader = ({ loading, children }) => {
	const classes = useStyles();

	return (
		<>
			{loading ? (
				// Reuse the real header's Card + class so padding, borders and
				// the xs column-stack apply identically while loading.
				<Card square elevation={0} className={classes.ticketHeader}>
					<div className={classes.skeletonContact}>
						<Skeleton
							animation="wave"
							variant="circle"
							width={40}
							height={40}
						/>
						<div>
							<Skeleton animation="wave" width={110} height={20} />
							<Skeleton animation="wave" width={150} height={16} />
						</div>
					</div>
					<div className={classes.skeletonActions}>
						<Skeleton
							animation="wave"
							variant="rect"
							width={96}
							height={32}
							style={{ borderRadius: 16 }}
						/>
						<Skeleton
							animation="wave"
							variant="circle"
							width={32}
							height={32}
						/>
					</div>
				</Card>
			) : (
				<Card square elevation={0} className={classes.ticketHeader}>
					{children}
				</Card>
			)}
		</>
	);
};

export default TicketHeader;
